import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Image,
  Box,
  Loader,
  Center,
  Paper,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBolt,
  IconHeart,
  IconLock,
  IconBuildingBank,
  IconShieldCheck,
  IconFingerprint,
  IconUserCheck,
} from "@tabler/icons-react";
import { DataContext } from "../../../context/DataContext";
import { isMobile } from "react-device-detect";
import QRCode from "qrcode.react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../../utils/edge";

const defaultFeatures = [
  { icon: IconBolt, text: "Your bank instantly verifies you" },
  { icon: IconLock, text: "Keep your data safe and secure" },
  { icon: IconHeart, text: "No need to provide documents" },
];

const TrinsicStart = ({
  providerName,
  providerDisplayName,
  logo,
  logoWidth = 100,
  testUrl = null,
  title = "Verify With Your Bank",
  TitleIcon = IconBuildingBank,
  features = defaultFeatures,
  poweredByText = "Powered by",
  scanText = "Scan to continue",
  getStartedText = "Get Started",
}) => {
  const { settings, data } = useContext(DataContext);
  const theme = useMantineTheme();
  const [url, setUrl] = useState(testUrl);
  const [requestId, setRequestId] = useState(null);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  const handleVerifyClick = () => {
    window.open(url, "_blank");
  };

  // Polling function for edge mode
  const pollForUrl = async (requestId) => {
    const maxAttempts = 100; // 5 minutes = 300 seconds / 3 seconds per attempt
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.request_url) {
            setUrl(result.request_url);
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No URL received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (url) return;
    const fetchUrl = async () => {
      data.provider = providerName;
      // Store provider name for retrieval on return
      sessionStorage.setItem("trinsic_provider", providerName);
      // Store request_id for edge mode return flow
      if (settings?.request_id) {
        sessionStorage.setItem("edge_request_id", settings.request_id);
      }
      try {
        // Check if edge mode is enabled
        let apiUrl;
        let requestData = { ...data };

        if (data?.edge === true) {
          apiUrl = getEdgeQueueUrl();
          // Add queue parameter for idmax
          requestData.queue = "idmax";
        } else {
          apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
        }

        const response = await fetch(apiUrl, {
            method: "POST",
            body: JSON.stringify(requestData),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data_returned = await response.json();
        console.log("data", data_returned);

        if (data?.edge === true) {
          // In edge mode, we get a request_id and need to poll for the URL
          const reqId = data_returned.request_id || data.request_id;
          if (reqId) {
            setRequestId(reqId);
            console.log("Starting polling for request_id:", reqId);
            pollForUrl(reqId);
          } else {
            console.error("No request_id found in edge response");
          }
        } else {
          // In normal mode, we get the URL directly
          setUrl(data_returned.continuations.info.template.info.request_url);
        }
      } catch (error) {
        console.error("Error fetching status:", error);
      }
    };

    fetchUrl();
  }, [url, providerName]);

  return (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <Box>
          <Stack>
            <Stack align="center" spacing={0}>
              <Group spacing={6}>
                <TitleIcon size={28} />
                <Text fz="xl" fw={700}>
                  {title}
                </Text>
              </Group>

              <Text fz="sm" c="dimmed">
                {poweredByText} {providerDisplayName}
              </Text>
            </Stack>
            <Center mt="sm">
              <Paper
                p="lg"
                withBorder
                style={{
                  backgroundColor:
                    data?.styles?.components?.paper?.backgroundColor ??
                    (theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa")
                }}
              >
                <Stack>
                  {features.map((feature, index) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <Group noWrap key={index}>
                        <FeatureIcon size={20} />
                        <Text fz="md">{feature.text}</Text>
                      </Group>
                    );
                  })}
                </Stack>
              </Paper>
            </Center>
          </Stack>
          {!isMobile && (
            <Stack align="center" mt="xl">
              <Box
                style={{
                  height: 200,
                  width: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack>
                  {url ? <QRCode value={url} size={200} /> : <Loader />}
                </Stack>
              </Box>

              <Stack spacing={5}>
                <Text ta="center" fw={700}>
                  {scanText}
                </Text>
              </Stack>
            </Stack>
          )}
        </Box>
        <Box>
          {!isMobile ? (
            <Group position="center" mt="md" noWrap>
              <Image src={logo} width={logoWidth} />
            </Group>
          ) : (
            <Button
              size="lg"
              mt="lg"
              onClick={handleVerifyClick}
              fullWidth
            >
              {getStartedText}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default TrinsicStart;
